import {
  type Path,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import Button from '@ui/Button/Button'

import * as styles from './Callback.css'
import RedirectInElectronApp from './RedirectInElectronApp'
import parseState from './parseState'

export type { CallbackState } from './parseState'

export default function Callback() {
  const navigate = useNavigate()
  const { provider } = useParams<{ provider: string }>()
  const [searchParams] = useSearchParams()

  const { state, ...queryParams } = Object.fromEntries(searchParams.entries())
  const decodedState = state ? atob(state) : null
  const parsedState = decodedState ? parseState(decodedState) : null

  const createRedirectTo = (redirectTo: string): Pick<Path, 'pathname' | 'search'> => {
    const redirectUrl = new URL(redirectTo, window.location.origin)

    for (const [queryParamKey, queryParamValue] of Object.entries(queryParams)) {
      if (queryParamValue) {
        redirectUrl.searchParams.set(queryParamKey, queryParamValue)
      }
    }

    if (provider) {
      redirectUrl.searchParams.set('provider', provider)
    }

    return {
      pathname: redirectUrl.pathname,
      search: redirectUrl.search,
    }
  }

  if (typeof parsedState === 'string' && parsedState.startsWith('/')) {
    const redirectTo = createRedirectTo(parsedState)
    return <Navigate to={redirectTo} />
  }

  if (parsedState && typeof parsedState !== 'string') {
    const redirectTo = createRedirectTo(parsedState.redirectTo)

    if (parsedState.desktop) {
      return <RedirectInElectronApp to={redirectTo} />
    }

    return <Navigate to={redirectTo} />
  }

  // Handle marketplace-initiated OAuth flows (e.g. installing from Pipedrive's marketplace)
  // where no `state` is set because Quo didn't initiate the flow.
  if (provider) {
    const redirectTo = createRedirectTo(`/settings/integrations/${provider}`)
    return <Navigate to={redirectTo} />
  }

  const error =
    "We can't quite figure out how you ended up on this screen. If you were adding an integration, try going through the flow again."

  return (
    <div className={styles.root}>
      <div style={{ padding: '4rem 2rem 0' }}>{error}</div>
      <Button
        style={{ marginTop: 40 }}
        variant="ghost"
        onClick={() => navigate('/inbox')}
      >
        Go back to inbox
      </Button>
    </div>
  )
}
